'use client'

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type ReactNode,
} from 'react'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  PauseIcon,
  PlayIcon,
} from '@heroicons/react/24/outline'

interface CarouselSlide {
  id: string
  label: string
  content: ReactNode
}

interface CarouselSlidesProps {
  slides: CarouselSlide[]
  interval: number
}

export const CarouselSlides = ({ slides, interval }: CarouselSlidesProps) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)
  const [isHovering, setIsHovering] = useState(false)
  const [hasFocus, setHasFocus] = useState(false)
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false)
  const [pageVisible, setPageVisible] = useState(true)
  const rootRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const media = window.matchMedia('(prefers-reduced-motion: reduce)')
    const update = () => setPrefersReducedMotion(media.matches)
    update()
    media.addEventListener('change', update)
    return () => media.removeEventListener('change', update)
  }, [])

  useEffect(() => {
    const update = () => setPageVisible(document.visibilityState === 'visible')
    update()
    document.addEventListener('visibilitychange', update)
    return () => document.removeEventListener('visibilitychange', update)
  }, [])

  const autoRotationStopped =
    isPaused || isHovering || hasFocus || prefersReducedMotion || !pageVisible
  const visibleIndex = Math.min(currentIndex, Math.max(slides.length - 1, 0))

  useEffect(() => {
    if (slides.length <= 1 || autoRotationStopped) return
    const timer = window.setTimeout(() => {
      setCurrentIndex(previous => (previous + 1) % slides.length)
    }, interval)
    return () => window.clearTimeout(timer)
  }, [autoRotationStopped, currentIndex, interval, slides.length])

  const goToPrevious = useCallback(() => {
    setCurrentIndex(previous => (previous - 1 + slides.length) % slides.length)
  }, [slides.length])

  const goToNext = useCallback(() => {
    setCurrentIndex(previous => (previous + 1) % slides.length)
  }, [slides.length])

  const handleBlur = (event: FocusEvent<HTMLDivElement>) => {
    if (!rootRef.current?.contains(event.relatedTarget)) setHasFocus(false)
  }

  if (slides.length === 0) return null

  return (
    <div
      ref={rootRef}
      role="region"
      aria-roledescription="carousel"
      aria-label="Featured promotions"
      className="group relative h-full w-full"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onFocusCapture={() => setHasFocus(true)}
      onBlurCapture={handleBlur}
    >
      <div
        className="grid h-full w-full grid-cols-1 grid-rows-1"
        aria-live={autoRotationStopped ? 'polite' : 'off'}
      >
        {slides.map((slide, index) => {
          const active = index === visibleIndex
          return (
            <div
              key={slide.id}
              role="group"
              aria-roledescription="slide"
              aria-label={`${index + 1} of ${slides.length}: ${slide.label}`}
              aria-hidden={!active}
              inert={!active ? true : undefined}
              className="grid-area-1-1 h-full w-full transition-opacity duration-500 motion-reduce:transition-none"
              style={{
                gridArea: '1 / 1',
                opacity: active ? 1 : 0,
                zIndex: active ? 10 : 0,
                pointerEvents: active ? 'auto' : 'none',
              }}
            >
              {slide.content}
            </div>
          )
        })}
      </div>

      {slides.length > 1 ? (
        <>
          <button
            type="button"
            onClick={goToPrevious}
            className="absolute left-4 top-1/2 z-20 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white opacity-100 transition-opacity hover:bg-black/60 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
            aria-label="Previous slide"
          >
            <ChevronLeftIcon className="size-6" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={goToNext}
            className="absolute right-4 top-1/2 z-20 flex size-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/40 text-white opacity-100 transition-opacity hover:bg-black/60 md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100"
            aria-label="Next slide"
          >
            <ChevronRightIcon className="size-6" aria-hidden="true" />
          </button>

          <button
            type="button"
            onClick={() => setIsPaused(paused => !paused)}
            className="absolute right-3 top-3 z-20 flex size-9 items-center justify-center rounded-full bg-black/40 text-white hover:bg-black/60"
            aria-label={
              isPaused
                ? 'Resume automatic slide rotation'
                : 'Pause automatic slide rotation'
            }
            aria-pressed={isPaused}
          >
            {isPaused ? (
              <PlayIcon className="size-5" aria-hidden="true" />
            ) : (
              <PauseIcon className="size-5" aria-hidden="true" />
            )}
          </button>

          <div className="absolute bottom-3 left-0 right-0 z-20 flex justify-center space-x-2">
            {slides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => setCurrentIndex(index)}
                className={`size-3 cursor-pointer rounded-full border border-black/20 shadow-md transition-colors ${
                  index === visibleIndex
                    ? 'bg-white'
                    : 'bg-white/50 hover:bg-white/80'
                }`}
                aria-label={`Show slide ${index + 1}: ${slide.label}`}
                aria-current={index === visibleIndex ? 'true' : undefined}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  )
}
